import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Menu, X, Wrench } from "lucide-react";
import { NAV_LINKS, PHONE_DISPLAY, PHONE_TEL } from "@/lib/content";
import { scrollToId } from "@/lib/scroll";

export default function Header() {
  const [open, setOpen] = useState(false);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 left-0 right-0 z-50 border-b border-stone-200/80 bg-white/85 backdrop-blur-md"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 sm:px-10">
        <button
          data-testid="header-logo-button"
          onClick={() => window.__lenis?.scrollTo(0, { duration: 1.2 })}
          className="group flex items-center gap-3"
        >
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-900 text-amber-500 transition-transform duration-300 group-hover:-rotate-6">
            <Wrench className="h-5 w-5" />
          </span>
          <span className="text-left leading-tight">
            <span className="font-display block text-sm font-bold tracking-tight text-slate-900">
              Atlantic Corridor
            </span>
            <span className="font-mono-accent block text-[10px] uppercase tracking-[0.22em] text-slate-400">
              Property Maintenance
            </span>
          </span>
        </button>

        <nav className="hidden items-center gap-7 lg:flex" data-testid="header-nav">
          {NAV_LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className="text-sm font-medium text-slate-500 transition-colors duration-200 hover:text-slate-900"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            data-testid="header-phone-link"
            href={PHONE_TEL}
            className="hidden items-center gap-2 rounded-full border border-stone-200 bg-white px-4 py-2 text-sm font-semibold text-slate-900 transition-colors duration-200 hover:border-amber-500 md:flex"
          >
            <Phone className="h-3.5 w-3.5 text-amber-600" />
            {PHONE_DISPLAY}
          </a>
          <button
            data-testid="header-get-quote-button"
            onClick={() => go("quote")}
            className="rounded-full bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-amber-600 hover:shadow-lg hover:shadow-amber-600/25"
          >
            Get Free Quote
          </button>
          <button
            data-testid="header-mobile-menu-button"
            onClick={() => setOpen(!open)}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-stone-200 lg:hidden"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <motion.nav
          data-testid="header-mobile-nav"
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="border-t border-stone-200 bg-white px-6 py-4 lg:hidden"
        >
          {NAV_LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`mobile-nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className="block w-full py-3 text-left text-base font-medium text-slate-700"
            >
              {l.label}
            </button>
          ))}
        </motion.nav>
      )}
    </header>
  );
}
